import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { ReactComponent as SelectorDownIcon } from '../../assets/selector-down.svg';
import { ReactComponent as SelectorUpIcon } from '../../assets/selector-up.svg';
import useAxios from '../../hooks/useAxios';
import useIsMobile from '../../hooks/useIsMobile';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { modalState } from '../../states/modalState';
import { userState } from '../../states/userState';
import { alertMessage } from '../../utils';

type ProductType = 'hemochat_basic' | 'hemochat_pro' | 'hemochat_coin';

const InquiryModal = () => {
  const isMobile = useIsMobile();
  const { requestAxios } = useAxios();
  const [, setModalState] = useRecoilState(modalState);
  const [{ userInfo }] = useRecoilState(userState);
  const elRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  useOutsideClick({ ref: optionsRef, handler: () => setIsSelectOpen(false) });

  const [isSelectOpen, setIsSelectOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(userInfo?.email || '');
  const [periodValue, setPeriodValue] = useState<string>();
  const [selectedProduct, setSelectedProduct] = useState<ProductType>();
  const [value, setValue] = useState<string>();

  const OPTIONS = [
    { value: 'hemochat_basic', label: 'Basic - 9,900원' },
    { value: 'hemochat_pro', label: 'Pro - 19,900원' },
    { value: 'hemochat_coin', label: '코인 개별구매' },
  ];

  const closeModal = () =>
    setModalState({
      isOpen: false,
    });

  useOutsideClick({ ref: elRef, handler: closeModal });

  useEffect(() => {
    setIsSelectOpen(false);
  }, [selectedProduct]);

  const onClickSelectBtn = () => {
    console.log(isSelectOpen);

    if (!isSelectOpen) {
      setIsSelectOpen(true);
      return;
    }
  };

  const onClickConfirmBtn = async () => {
    if (!value) {
      alertMessage('문의하실 내용을 입력해주세요.');
      return;
    }

    const data = {
      order_date: periodValue?.replace(/\./g, '-') || '',
      product_type: selectedProduct || '',
      inquiry_content: value || '',
    };

    try {
      const { message } = await requestAxios<{ message: string }>(true, {
        url: '/payment/contact-us/',
        method: 'post',
        data,
      });

      alertMessage(message);
    } catch (err) {
      const error = (err as any).response.data;
      const entries = Object.entries(error);
      const errorMessage = entries.join('\n');

      alertMessage(errorMessage);
    } finally {
      closeModal();
    }
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target?.value;
    const RegNotNum = /[^0-9]/g;
    const onlyNum = value.replace(RegNotNum, ''); // 숫자가 아닌 경우 ''

    let DataFormat: any;
    let RegDateFmt: any;

    if (onlyNum.length <= 4) {
      // 000000 -> 0000-00
      DataFormat = '$1.$2';
      RegDateFmt = /([0-9]{4})([0-9]+)/;
    } else if (onlyNum.length <= 8) {
      // 00000000 -> 0000-00-00
      DataFormat = '$1.$2.$3';
      RegDateFmt = /([0-9]{4})([0-9]{2})([0-9]+)/;
    }

    const periodValue = onlyNum.replace(RegDateFmt, DataFormat);

    setPeriodValue(periodValue);
  };

  return (
    <ModalWrapper ref={elRef}>
      <ModalTitle>결제내역 문의하기</ModalTitle>
      <ModalInputWrapper>
        {/* <ModalInput
          disabled
          type="number"
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onChange={onChange}
          value={value}
        ></ModalInput> */}
        <ModalInput
          type="text"
          placeholder="이메일을 입력하세요."
          value={email}
          disabled
          onChange={(e) => setEmail(e.target.value)}
        />
        <ModalInput
          maxLength={10}
          onChange={onChange}
          value={periodValue}
          type="text"
          placeholder="결제일시 YYYY.MM.DD"
        />

        <SelectWrapper>
          <Select
            id="product"
            value={selectedProduct}
            style={{
              color: selectedProduct ? '' : '#999999',
              border: isSelectOpen ? '2px solid #39A2FF' : '',
            }}
            onClick={onClickSelectBtn}
          >
            {OPTIONS.find((o) => o.value === selectedProduct)?.label ||
              '결제상품을 선택하세요.'}
            <span>
              {isSelectOpen ? <SelectorUpIcon /> : <SelectorDownIcon />}
            </span>
          </Select>
          {isSelectOpen && (
            <OptionsWrapper ref={optionsRef}>
              {OPTIONS.map((option) => (
                <Option
                  key={option.value}
                  onClick={() => {
                    setSelectedProduct(option.value as ProductType);
                  }}
                  style={{
                    backgroundColor:
                      option.value === selectedProduct ? '#ebf1ff' : '',
                  }}
                >
                  {option.label}
                </Option>
              ))}
            </OptionsWrapper>
          )}
        </SelectWrapper>

        <ModalTextarea
          style={{
            height: '116px',
          }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="문의하실 내용을 입력하세요."
        />
      </ModalInputWrapper>
      <ModalButtonWrapper $isMobile={isMobile}>
        <ModalButton
          $isMobile={isMobile}
          style={{ backgroundColor: '#ebebeb' }}
          onClick={() => closeModal()}
        >
          취소
        </ModalButton>
        <ModalButton
          $isMobile={isMobile}
          style={{ backgroundColor: '#1a2e52', color: 'white' }}
          onClick={onClickConfirmBtn}
        >
          확인
        </ModalButton>
      </ModalButtonWrapper>
    </ModalWrapper>
  );
};

export default InquiryModal;

const ModalWrapper = styled.div`
  width: 360px;
  height: 466px;
  background-color: #fff;
  border-radius: 10px;
  padding: 40px 20px 30px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ModalTitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  color: black;
  text-align: center;
`;

const ModalInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModalInput = styled.input`
  width: 100%;
  height: 46px;
  border: 1px solid #dcdcdc;
  font-size: 14px;
  color: #333333;
  padding-left: 18px;
  border-radius: 6px;
  outline: none;

  &::placeholder {
    color: #999999;
  }
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  height: 46px;
  border: 1px solid #dcdcdc;
  font-size: 14px;
  color: #333333;
  padding: 18px;
  border-radius: 6px;
  outline: none;
  font-family: Pretendard;

  &::placeholder {
    color: #999999;
  }
`;

const SelectWrapper = styled.div`
  width: 100%;
  height: 46px;
  position: relative;
`;

const Select = styled.button`
  background-color: transparent;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 46px;
  border: 1px solid #dcdcdc;
  font-size: 14px;
  color: #333333;
  outline: none;
  padding: 0 18px;
  border-radius: 6px;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalButtonWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  gap: 10px;
  width: 100%;
  height: 46px;
`;

const ModalButton = styled.button<{ $isMobile: boolean }>`
  border: none;
  width: 155px;
  height: 100%;
  outline: none;
  cursor: pointer;
  color: black;
  border-radius: 6px;
  font-size: 14px;
`;

const OptionsWrapper = styled.div`
  position: absolute;
  top: 49px;
  left: 0;
  border: 1px solid #dcdcdc;
  border-radius: 6px;
  width: 320px;
  height: 127px;
  background-color: white;
`;

const Option = styled.div`
  width: 100%;
  height: 42px;
  display: flex;
  justify-content: start;
  padding-left: 18px;
  align-items: center;
  color: #333333;
  font-size: 14px;
  cursor: pointer;
`;
